/**
 * V67: 휴직 현황 페이지
 *
 * - 탭: 현재 휴직 / 종료된 휴직
 * - BE listCurrentLeaveOfAbsences callable (statusFilter='current'|'ended')
 * - SSoT 정책은 BE 단독 검증 (FE 재계산 금지)
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertCircle, Filter } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';

interface LoaItem {
  loaId: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  companyCode: string;
  orgUnitId: string | null;
  orgUnitName: string | null;
  type: string;
  typeName: string;
  startDate: string;
  expectedReturnDate: string | null;
  actualReturnDate: string | null;
  status: string;
  isCurrent: boolean;
  reason: string | null;
  elapsedDays: number;
  remainingDays: number | null;
  totalPlannedDays: number | null;
  displayEndDate: string | null;
  totalDays: number | null;
  returnWorkDate: string | null;
  bucket: 'current' | 'ended';
  createdAt: string | null;
  updatedAt: string | null;
}

interface ListResponse {
  success: boolean;
  scope: string;
  todayKst: string;
  count: number;
  items: LoaItem[];
}

const STATUS_LABEL: Record<string, string> = {
  approved: '승인됨',
  inProgress: '진행 중',
  returned: '복귀 완료',
};

type Tab = 'current' | 'ended';

export function HrLeaveOfAbsencesPage() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const [tab, setTab] = useState<Tab>('current');
  const [items, setItems] = useState<LoaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchText, setSearchText] = useState('');
  const [filterCompany, setFilterCompany] = useState<'ALL' | 'KR' | 'CN'>('ALL');
  const [filterType, setFilterType] = useState<string>('all');

  const isGlobalAdmin = useMemo(() => {
    const roles = (user as any)?.roles || [];
    return roles.includes('system_admin') || roles.includes('role_global');
  }, [user]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<
        { companyCode?: 'KR' | 'CN' | null; search?: string | null; statusFilter?: Tab },
        ListResponse
      >(getFunctions(), 'listCurrentLeaveOfAbsences');
      const res = await fn({
        companyCode: filterCompany === 'ALL' ? null : filterCompany,
        search: searchText.trim() || null,
        statusFilter: tab,
      });
      setItems(res.data.items || []);
    } catch (e: any) {
      setError(e?.message || '휴직 현황 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [filterCompany, searchText, tab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const typeOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const it of items) {
      if (it.type) map.set(it.type, it.typeName || it.type);
    }
    return Array.from(map.entries()).map(([k, v]) => ({ value: k, label: v }));
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (filterType !== 'all' && it.type !== filterType) return false;
      return true;
    });
  }, [items, filterType]);

  return (
    <div style={{ padding: '24px', height: '100%', overflowY: 'auto' }}>
      {/* 헤더 — 버튼은 윗줄 / 제목은 아래줄 */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>
        <button
          type="button"
          onClick={() => navigate('/hr/leave-overview')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            background: 'white',
            border: '1px solid var(--color-gray-300)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          <ArrowLeft size={16} /> 휴가 현황으로
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-gray-800)', margin: 0 }}>
          휴직 현황
        </h1>
      </div>
      <p style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginBottom: '16px' }}>
        {tab === 'current' ? '현재 휴직 중인 직원 목록' : '복귀 완료/종료된 휴직 이력'}
      </p>

      {/* 탭 */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '16px',
          borderBottom: '1px solid var(--color-gray-200)',
        }}
      >
        {(['current', 'ended'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{
              padding: '10px 18px',
              background: 'none',
              border: 'none',
              borderBottom: tab === t ? '2px solid var(--color-blue-600)' : '2px solid transparent',
              color: tab === t ? 'var(--color-blue-600)' : 'var(--color-gray-600)',
              fontWeight: tab === t ? 600 : 400,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {t === 'current' ? '현재 휴직' : '종료된 휴직'}
          </button>
        ))}
      </div>

      {/* 필터 */}
      <div
        style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '16px',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Filter size={16} style={{ color: 'var(--color-gray-600)' }} />
          <input
            type="text"
            placeholder="이름 / 사번 / 조직 검색..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              width: '260px',
            }}
          />
        </div>

        {isGlobalAdmin && (
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value as 'ALL' | 'KR' | 'CN')}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
            }}
          >
            <option value="ALL">전체 회사</option>
            <option value="KR">KR</option>
            <option value="CN">CN</option>
          </select>
        )}

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid var(--color-gray-300)',
            borderRadius: '6px',
            fontSize: '14px',
            background: 'white',
          }}
        >
          <option value="all">전체 휴직종류</option>
          {typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            background: loading ? 'var(--color-gray-300)' : 'var(--color-blue-600)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            marginLeft: 'auto',
          }}
        >
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> 새로고침
        </button>
      </div>

      {/* 통계 */}
      <div
        style={{
          background: 'white',
          padding: '16px 20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '16px',
          display: 'flex',
          gap: '24px',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
            {tab === 'current' ? '현재 휴직 중' : '종료된 휴직'}
          </div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-blue-600)' }}>
            {items.length}건
          </div>
        </div>
        {(filterType !== 'all' || searchText) && (
          <div>
            <div style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>필터 결과</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-gray-800)' }}>
              {filteredItems.length}건
            </div>
          </div>
        )}
      </div>

      {/* 에러 */}
      {error && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: '6px',
            marginBottom: '16px',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
          }}
        >
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {/* 테이블 */}
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'auto',
        }}
      >
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
            조회 중...
          </div>
        ) : filteredItems.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
            {tab === 'current' ? '현재 휴직 중인 직원이 없습니다.' : '종료된 휴직 이력이 없습니다.'}
          </div>
        ) : tab === 'current' ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ background: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-200)' }}>
                <th style={th}>직원명</th>
                <th style={th}>사번</th>
                <th style={th}>회사</th>
                <th style={th}>조직</th>
                <th style={th}>휴직종류</th>
                <th style={th}>시작일</th>
                <th style={th}>종료일</th>
                <th style={th}>복직일</th>
                <th style={th}>상태</th>
                <th style={th}>사유</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((it) => (
                <tr key={it.loaId} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                  <td style={td}>{it.employeeName || '-'}</td>
                  <td style={td}>{it.employeeNumber || '-'}</td>
                  <td style={td}>{it.companyCode}</td>
                  <td style={td}>{it.orgUnitName || '-'}</td>
                  <td style={td}>{it.typeName || it.type}</td>
                  <td style={td}>{it.startDate || '-'}</td>
                  <td style={td}>{it.expectedReturnDate || '-'}</td>
                  <td style={td}>{it.returnWorkDate || '-'}</td>
                  <td style={td}>
                    <span style={statusPill(it.status)}>
                      {STATUS_LABEL[it.status] || it.status}
                    </span>
                  </td>
                  <td style={{ ...td, maxWidth: '240px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={it.reason || ''}>
                    {it.reason || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          // ended tab
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ background: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-200)' }}>
                <th style={th}>직원명</th>
                <th style={th}>사번</th>
                <th style={th}>회사</th>
                <th style={th}>조직</th>
                <th style={th}>휴직종류</th>
                <th style={th}>시작일</th>
                <th style={th}>종료일</th>
                <th style={th}>복직일</th>
                <th style={thRight}>총 휴직일수</th>
                <th style={th}>상태</th>
                <th style={th}>사유</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((it) => (
                <tr key={it.loaId} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                  <td style={td}>{it.employeeName || '-'}</td>
                  <td style={td}>{it.employeeNumber || '-'}</td>
                  <td style={td}>{it.companyCode}</td>
                  <td style={td}>{it.orgUnitName || '-'}</td>
                  <td style={td}>{it.typeName || it.type}</td>
                  <td style={td}>{it.startDate || '-'}</td>
                  <td style={td}>{it.displayEndDate || '-'}</td>
                  <td style={td}>{it.returnWorkDate || '-'}</td>
                  <td style={tdRight}>
                    {it.totalDays !== null ? `${it.totalDays}일` : '-'}
                  </td>
                  <td style={td}>
                    <span style={statusPill(it.status)}>
                      {STATUS_LABEL[it.status] || it.status}
                    </span>
                  </td>
                  <td style={{ ...td, maxWidth: '240px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={it.reason || ''}>
                    {it.reason || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function statusPill(status: string): React.CSSProperties {
  const map: Record<string, { bg: string; fg: string }> = {
    inProgress: { bg: '#dbeafe', fg: '#1e40af' },
    returned: { bg: '#dcfce7', fg: '#166534' },
    approved: { bg: '#fef3c7', fg: '#92400e' },
  };
  const c = map[status] || { bg: '#f3f4f6', fg: '#374151' };
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    background: c.bg,
    color: c.fg,
    fontSize: '12px',
  };
}

const th: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  color: 'var(--color-gray-700)',
  fontSize: '13px',
  whiteSpace: 'nowrap',
};
const thRight: React.CSSProperties = { ...th, textAlign: 'right' };
const td: React.CSSProperties = { padding: '10px 12px', color: 'var(--color-gray-800)', fontSize: '14px' };
const tdRight: React.CSSProperties = { ...td, textAlign: 'right' };
